import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./About.css";

import I18nText from "../components/I18nText";
import BrandMark from "../components/BrandMark";
import { track } from "../lib/analytics";

const MARK_SACKS_PHOTO = "/assets/mark_sacks_coral.png";

function Hero() {
    const { t } = useTranslation();
    return (
        <section className="about-hero" data-testid="about-hero">
            <div className="section-label mono">{t("about.label")}</div>
            <h1>
                <I18nText i18nKey="about.h1" />
            </h1>
            <p className="about-hero-lede">
                <I18nText i18nKey="about.lede" />
            </p>
        </section>
    );
}

function Frustration() {
    const { t } = useTranslation();
    return (
        <section className="about-frustration" data-testid="about-frustration">
            <div className="about-frustration-inner">
                <div className="about-frustration-grid">
                    <h2><I18nText i18nKey="about.frustration.h2" /></h2>
                    <div className="about-frustration-text">
                        <p>{t("about.frustration.p1")}</p>
                        <p><I18nText i18nKey="about.frustration.p2" /></p>
                        <p>{t("about.frustration.p3")}</p>
                    </div>
                </div>
            </div>
        </section>
    );
}

function Founder() {
    const { t } = useTranslation();
    return (
        <section className="about-founder" data-testid="about-founder">
            <div className="section-label mono">{t("about.founder.label")}</div>
            <div className="about-founder-grid">
                <div className="about-founder-photo" data-testid="founder-photo">
                    <img src={MARK_SACKS_PHOTO} alt={`${t("about.founder.name")} — ${t("about.founder.role")}`} />
                    <div className="about-founder-photo-caption">
                        <div className="fc-name">{t("about.founder.name")}</div>
                        <div className="fc-role mono">{t("about.founder.role")}</div>
                    </div>
                </div>
                <div className="about-founder-story">
                    <h3><I18nText i18nKey="about.founder.h3" /></h3>
                    <p>{t("about.founder.p1")}</p>
                    <p data-testid="founder-origin-p1">{t("about.founder.originP1")}</p>
                    <p data-testid="founder-origin-p2">{t("about.founder.originP2")}</p>
                    <p>{t("about.founder.p2")}</p>
                    <p className="muted">{t("about.founder.p3")}</p>
                    <div className="team-note" data-testid="team-note">
                        {t("about.founder.teamNote")}
                    </div>
                </div>
            </div>
        </section>
    );
}

function Transparency() {
    const { t } = useTranslation();
    return (
        <section className="about-transparency" data-testid="about-transparency">
            <div className="about-transparency-inner">
                <div className="section-label mono">{t("about.transparency.label")}</div>
                <h2><I18nText i18nKey="about.transparency.h2" /></h2>
                <div className="about-transparency-body">
                    <div>
                        <p><I18nText i18nKey="about.transparency.p1" /></p>
                        <p>{t("about.transparency.p2")}</p>
                    </div>
                    <div>
                        <p>{t("about.transparency.p3")}</p>
                        <p>{t("about.transparency.p4")}</p>
                        <div className="about-transparency-callout">
                            {t("about.transparency.callout")}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function Commitments() {
    const { t } = useTranslation();
    const items = t("about.commitments.items", { returnObjects: true }) || [];
    return (
        <section className="about-commitments" data-testid="about-commitments">
            <div className="section-label mono">{t("about.commitments.label")}</div>
            <h2><I18nText i18nKey="about.commitments.h2" /></h2>
            <p className="about-commitments-lede">{t("about.commitments.lede")}</p>
            <div className="commitment-list">
                {items.map((c) => (
                    <div className="commitment" key={c.num} data-testid={`commitment-${c.num}`}>
                        <div className="commitment-num mono">{c.num}</div>
                        <h4>{c.title}</h4>
                        <p>{c.body}</p>
                    </div>
                ))}
            </div>
        </section>
    );
}

function Manifesto() {
    const { t } = useTranslation();
    const lines = t("about.manifesto.lines", { returnObjects: true }) || [];
    return (
        <section className="about-manifesto" data-testid="about-manifesto">
            <div className="about-manifesto-inner">
                <div className="section-label mono about-manifesto-label">
                    {t("about.manifesto.label")}
                </div>
                <div className="manifesto-lines">
                    {lines.map((line, i) => (
                        <span className="line" key={i}>
                            <I18nText
                                i18nKey={`about.manifesto.lines.${i}`}
                            />
                        </span>
                    ))}
                </div>
                <div className="manifesto-attribution mono">{t("about.manifesto.attribution")}</div>
            </div>
        </section>
    );
}

function FinalCta() {
    const { t } = useTranslation();
    const onClick = (role) => () => track("about_cta_click", { role });
    return (
        <section className="about-final-cta" data-testid="about-final-cta">
            <h2><I18nText i18nKey="about.finalCta.h2" /></h2>
            <p className="sub">{t("about.finalCta.sub")}</p>
            <div className="about-dual-cta">
                <Link to="/employers" data-testid="about-cta-employer" onClick={onClick("employer")}>
                    <div className="dual-cta-label mono">{t("about.finalCta.employerLabel")}</div>
                    <div className="dual-cta-title">{t("about.finalCta.employerTitle")}</div>
                </Link>
                <Link to="/employees" data-testid="about-cta-employee" onClick={onClick("employee")}>
                    <div className="dual-cta-label mono">{t("about.finalCta.employeeLabel")}</div>
                    <div className="dual-cta-title">{t("about.finalCta.employeeTitle")}</div>
                </Link>
            </div>
        </section>
    );
}

export default function About() {
    const { t } = useTranslation();
    useEffect(() => {
        track("about_view", {});
        document.title = t("about.title");
    }, [t]);

    return (
        <div data-testid="about-page">
            <Hero />
            <Frustration />
            <Founder />
            <Transparency />
            <Commitments />
            <Manifesto />
            <FinalCta />
        </div>
    );
}

// keep BrandMark imported at module level so I18nText can also reach it through JSX tag
// (the import is required for tree-shaking to keep the component reachable when <brand/> is used).
export { BrandMark };
